import Axios from 'axios';
import { counterInfoGet } from '@/utils/counter/data';
import { getUrlParams } from '@/utils/base';

// 默认 axios 实例请求配置
const configDefault = {
  timeout: 0,
  baseURL: '/api',
};

/** 收银台下单相关接口 */
const isCounterOrderApi = url => {
  const u = url || '';
  return u.includes('placePayOrder') || u.includes('placeTransferOrder') || u.includes('placeWallet');
};

/** 原无 toast 的下单错误：仅展示右下角 code */
const showOrderApiCode = code => {
  if (code == null || code === '') return;
  window.$toast({ text: '', code, type: 'error', duration: 1500 });
};

/** code、msg 均未返回时才兜底为未知异常【520】；任一有值则原样拼接 */
const formatCounterErrToast = (msg, code) => {
  const hasMsg = msg != null && String(msg).trim() !== '';
  const hasCode = code != null && code !== '';
  if (!hasMsg && !hasCode) {
    return { msg: '未知异常', code: 520, text: '未知异常【520】' };
  }
  return { msg, code, text: `${msg}【${code}】` };
};

class Http {
  constructor(config) {
    Http.axiosConfigDefault = config;
    Http.axiosInstance = Axios.create(config);
    this.httpInterceptorsRequest();
    this.httpInterceptorsResponse();
  }
  // 当前实例
  static axiosInstance;
  // 请求配置
  static axiosConfigDefault;

  // 请求拦截
  httpInterceptorsRequest() {
    Http.axiosInstance.interceptors.request.use(
      config => {
        let { mchId, sourceMemberId, accessToken } = counterInfoGet();

        if (!accessToken) {
          const urlParams = getUrlParams();

          if (urlParams && urlParams.accessToken) {
            accessToken = urlParams.accessToken;
          }
        }

        // 发送请求前，可在此携带 token
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ` + accessToken;
          config.headers['Accept-Language'] = 'zh-CN';
        }
        if (config.method == 'get') {
          if (config.params) {
            if (config.contentType) {
              config.headers['Content-Type'] = config.contentType;
            }
          }
        } else {
          if (config.url != '/order/test/auth/jumpForTest') {
            if (!config.iSupload) {
              if (config.contentType) {
                config.headers['Content-Type'] = config.contentType;
              } else {
                config.headers['Content-Type'] = 'application/json'; // 保持 Content-Type 为 JSON
              }
            }
          }
        }
        config.headers['Merchant'] = mchId;
        config.headers['SourceMemberId'] = sourceMemberId;

        return config;
      },
      error => {
        window.$toast({
          text: error.message,
          type: 'error',
          duration: 1500,
        });
        return Promise.reject(error);
      }
    );
  }

  // 响应拦截
  httpInterceptorsResponse() {
    Http.axiosInstance.interceptors.response.use(
      response => {
        // 与后端协定的返回字段
        // isCounterApi 变量判断是否是收银台counter接口
        const isCounterApi = response.config.url.includes('/counter/') || response.config.url.includes('/order/chat/auth/');

        let { code, msg, data } = response.data;
        const { needAllData, allowEmptySuccess } = response.config;

        // 是否需要返回所有数据
        if (needAllData) {
          const d = response.data;
          const u = response.config.url || '';
          // placeWallet：100011 页面自 toast 并传 code；其余静默错误补 code
          if (isCounterOrderApi(u) && d && Reflect.has(d, 'code') && d.code !== 0 && d.code !== 100011) {
            showOrderApiCode(d.code);
          }
          return response.data;
        }

        // 判断请求是否成功 （code 200 请求成功）
        const isSuccess = (data || data === 0 || data === '') && Reflect.has(response.data, 'code') && code === 0;

        if (isSuccess) {
          return data;
        } else {
          // 仅显式 allowEmptySuccess 的接口：code=0 且 data 为 null/等时仍视为成功、不弹 msg（不扩散到全 counter）
          if (isCounterApi && allowEmptySuccess && Reflect.has(response.data, 'code') && code === 0) {
            return data;
          }

          if (isCounterApi) {
            const reqUrl = response.config.url || '';
            // 仅 alipayJobLoginToken 预取/静默刷新：失败不 toast，不影响其他接口
            if (reqUrl.includes('alipayJobLoginToken') && response.config.alipayJobSilent) {
              return false;
            }
            const orderApi = isCounterOrderApi(reqUrl);

            if (code == 6106 && reqUrl.includes('getProcessStatic')) {
              return;
            }
            if (code == 6360 || msg === '请勿重复提交') {
              if (orderApi) {
                showOrderApiCode(code);
              }
              return;
            }

            // 如果是取款 特定错误信息 则修改
            if (response.config.url.includes('placeTransferOrder') && msg && msg.includes('系统内部错误')) {
              window.$toast({
                text: `流水不足，可能结算延迟10分钟后重试【${code}】`,
                type: 'error',
                duration: 1500,
              });
              return false;
            }

            // 收银台登录过期相关逻辑处理
            if ([6228, 6229, 6230, 6231, 6102, 1501].includes(code)) {
              // 如果当前页面是充值页面，则提示充值通道访问超时，请重试！，其他提示msg内容
              const text = location.pathname.includes('/counter/deposit') ? '网络繁忙 请稍后重试' : msg;
              window.$toast({
                text: `${text}【${code}】`,
                type: 'error',
                duration: 1500,
              });

              setTimeout(() => {
                handleBack({
                  value: 'loginTimeout',
                  msg: '登录过期返回',
                });
              }, 1500);
              return;
            }
            if (code == 6340) {
              window.$toast({
                text: `${msg}【${code}】`,
                type: 'error',
                duration: 1500,
              });
              return;
            }

            if (response.config.url.includes('merchant/placePayOrder') && typeof msg === 'string') {
              if (msg.trim().startsWith('仅支持')) {
                showOrderApiCode(code);
                return false;
              }
              // 存款的特殊错误状态
              if ([6370, 6371, 6326, 2220005, 2220006, 1110013].includes(code)) {
                return `${msg}【${code}】`;
              }

              return `支付商家未接单，请更换通道尝试【${code}】`;
            }

            if (!(typeof msg === 'string' && msg.trim().startsWith('仅支持'))) {
              window.$toast({
                text: formatCounterErrToast(msg, code).text,
                type: 'error',
                duration: 1500,
              });
            }

            // 提现的特殊错误状态
            if (code == 6349) {
              return 0;
            }

            // 报错了返回false
            return false;
          }

          if (msg === '请勿重复提交') {
            return;
          }
          if (response.config.url.includes('queryMemberInfo')) {
            return;
          }

          return Promise.reject(response.data);
        }
      },
      error => {
        // 如果当前是获取充值列表接口，则提示操作过于频繁，请稍后重试!
        if (error.config.url === '/counter/front/merchant/paymentMethod') {
          window.$toast({
            text: '操作过于频繁，请稍后重试！',
            type: 'error',
            duration: 1500,
          });

          setTimeout(() => {
            handleBack({
              value: 'error',
              msg: '操作过于频繁，请稍后重试！',
            });
          }, 1500);
          return Promise.reject(error);
        }

        // HTTP 状态码
        const status = error.response?.status;
        const errUrl = error.config?.url || '';
        if (errUrl.includes('alipayJobLoginToken') && error.config?.alipayJobSilent) {
          return Promise.reject(error);
        }
        if (status) {
          // placePayOrder：HTTP 430 限频（非 body.code）
          if (errUrl.includes('merchant/placePayOrder') && status === 430) {
            window.$toast({
              text: `操作频繁，稍后重试【 ${status} 】`,
              type: 'error',
              duration: 1500,
            });
            return Promise.reject(error);
          }

          // 429: Too Many Requests
          if (status === 429) {
            console.log('==== 限流 ====');
            return Promise.reject(error);
          }

          window.$toast({
            text: `网络连接失败，请稍后重试【 ${status} 】`,
            type: 'error',
            duration: 1500,
          });
        }

        return Promise.reject(error);
      }
    );
  }

  // 通用请求函数
  request(paramConfig) {
    const config = { ...Http.axiosConfigDefault, ...paramConfig };
    return new Promise((resolve, reject) => {
      Http.axiosInstance
        .request(config)
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  // 设置超时处理
  setupTimeoutHandler() {
    Http.axiosInstance.interceptors.response.use(
      response => response,
      error => {
        if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
          // 超时时返回 null
          return Promise.resolve(false);
        }
        return Promise.reject(error);
      }
    );
  }
}

export const http = new Http(configDefault);

// 有超时时间的请求
export const httpWithTimeout = new Http({
  ...configDefault,
  timeout: 15000,
});

// 为超时请求添加超时处理
httpWithTimeout.setupTimeoutHandler();

/**
 * 登录过期返回
 */
const handleBack = (obj = {}) => {
  // 移除登录的缓存
  localStorage.removeItem('counterInfo');

  const { plat } = counterInfoGet();

  if (plat === 1) {
    // 针对cocos的app要求 进行返回
    document.location = `postback://type=back@value=${obj.value || 'loginTimeout'}`;

    return;
  }

  // 登录过期返回通知
  window.parent.postMessage(
    {
      type: 'back',
      msg: obj.msg || '登录过期返回',
      value: obj.value || 'loginTimeout',
    },
    '*'
  );
};
